/**
 * DynamicAppBundle — dynamically loads a pack's ESM bundle from the kernel
 * at `${serverUrl}/_apps/<id>/bundle.mjs` and renders its default export as
 * a native React component inside the desktop shell.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * PRE-FLIGHT EVIDENCE — khal-stale-ui-fix Group 4
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Date: 2026-04-09
 * Tauri version: 2.x (tauri = "2", tauri-build = "2" — see tauri/Cargo.toml)
 * Target webviews: WKWebView (macOS, document origin `tauri://localhost`),
 *                  WebView2 (Windows, document origin `http://tauri.localhost`),
 *                  WebKitGTK (Linux, document origin `tauri://localhost`)
 *
 * -- Kernel plumbing verification (ran `curl` against dev.khal.namastex.io) --
 * $ curl -sI https://dev.khal.namastex.io/api/health
 *     HTTP/2 200  (kernel is live)
 *
 * Note: dev deployment has NOT yet picked up khal-os/deploy#52 (G1 image
 * rebuild) at the moment this comment was written — `/_apps/*` currently
 * 307s to WorkOS because the pre-G1 proxy matcher has not been reloaded.
 * Once G6 (rebuild + deploy) lands the route will be public, per
 * `core/src/proxy.ts` which explicitly excludes `_apps/` from the auth
 * matcher (see `/((?!…|_apps/|…).*)`).
 *
 * -- Kernel route guarantees (verified from source — core commit 9f57220) --
 * `core/src/app/_apps/[id]/[...path]/route.ts`:
 *   • GET returns `Content-Type: application/javascript` for `.mjs`
 *   • Adds CORS `Access-Control-Allow-Origin` for `tauri://localhost`,
 *     `http://tauri.localhost`, `https://tauri.localhost`
 *   • Path-traversal guard + 10 MB size limit + 30 s stream timeout
 *
 * -- CSP verification (tauri/tauri.conf.json) --
 *   default-src: 'self' http://localhost:* https://* ws://localhost:* wss://* ipc: tauri:
 *   script-src:  'self' 'unsafe-inline' 'unsafe-eval' http://localhost:* https://*
 *   connect-src: 'self' http://localhost:* https://* ws://localhost:* wss://* ipc: tauri:
 *   → Both native `import()` AND `fetch()` from `https://dev.khal.namastex.io`
 *     are permitted by CSP. Neither is a CSP blocker.
 *
 * -- DevTools `import()` smoke test (wish pre-flight step 3) --
 * NOT EXECUTED interactively. Running `pnpm tauri:dev` and pasting JS into
 * the webview DevTools is a manual human step that the agent executing this
 * group has no way to perform headlessly. Because of that, this component
 * ships a **dual-strategy loader** that eliminates the unknown:
 *
 *   1. Try native `await import(/* @vite-ignore *\/ bundleUrl)` first
 *      — the fastest path, zero copy, preserves source maps.
 *   2. If native import throws for ANY reason (cross-origin policy, CSP,
 *      MIME mismatch, webview bug, …), fall back to `fetch` + `Blob` URL.
 *      `blob:` URLs are same-origin by definition in every webview, so the
 *      fallback bypasses every cross-origin/CSP edge case. The already-
 *      whitelisted CORS header makes the fetch succeed.
 *
 * CHOSEN PATH: **Dual strategy (native → blob fallback)**.
 *
 * Why: the risk asymmetry. If we picked native-only and the webview rejects
 * cross-origin import, the entire desktop shell would render blank apps in
 * production — the exact regression this wish is trying to fix. The blob
 * fallback is a few lines of code and eliminates that failure mode at the
 * cost of one extra `fetch()` round-trip in the pessimistic case.
 *
 * First-open validation (wish acceptance criterion) remains manual:
 * `pnpm tauri:dev` → sign in → open Terminal → xterm prompt appears. See the
 * Group 4 PR body for the screenshot/log capture.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useDesktopStore } from '@khal-os/desktop-shell';
import type { ComponentType } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { getEnterpriseConfig } from '../lib/tauri';
import { Sentry } from '../sentry';

/**
 * Build the API base URL a pack should use to reach its own BFF, then expose
 * it on `window` for the pack bundle to read at module init.
 *
 * Why this exists: pack frontends ship hardcoded `fetch('/api/…')` calls
 * expecting same-origin reach to their BFF (the production deployment model
 * where the dashboard + backend share a hostname via ingress). On Khal OS
 * the desktop shell mounts the pack bundle at the kernel origin (e.g.
 * `https://<tailnet>:5173`) but each pack's BFF is exposed on a different
 * port (the `backend.ports[0]` from `khal-app.json`). Without a runtime
 * override, the pack's relative-URL fetches hit the desktop's Vite SPA and
 * 404 with HTML — which surfaces in the user UI as indefinite spinners or
 * "Invalid response" errors.
 *
 * Packs that have adopted the configurable-base-URL convention
 * (namastexlabs/eugenia-metrics-pack#73) read these `window` globals as the
 * top-priority override in their own `API_BASE` resolution:
 *
 *   - `window.__KHAL_PACK_API_BASE__` — generic, future-proof
 *   - `window.__EUGENIA_API_BASE__`   — pack-specific compatibility shim,
 *                                       removed once every pack reads the
 *                                       generic global
 *
 * The base URL is derived from the resolved kernel URL (so it inherits the
 * tailnet hostname / scheme the operator is actually browsing on) and the
 * pack's declared backend port. No hardcoded hostnames or ports.
 */
function injectPackApiBase(appId: string, kernelServerUrl: string): void {
	if (typeof window === 'undefined') return;
	let backendPort: number | undefined;
	try {
		const row = useDesktopStore.getState().installedAppRowsBySlug[appId];
		const manifest = row?.installedManifestJson as
			| { backend?: { ports?: unknown } }
			| undefined;
		const ports = manifest?.backend?.ports;
		if (Array.isArray(ports) && typeof ports[0] === 'number') {
			backendPort = ports[0];
		}
	} catch {
		// installedAppRowsBySlug may be empty if the dynamic registry hasn't
		// loaded yet (offline / pre-NATS-connect). Fall through with no
		// injection — the pack's same-origin fallback still produces a
		// visible error rather than a hang once the pack-side defensive
		// catch lands (eugenia-metrics-pack#76).
	}
	if (!backendPort) return;
	// Replace the kernel URL's port with the pack's backend port. Use URL
	// parsing rather than string surgery so weird kernel URLs (paths, auth)
	// degrade cleanly rather than producing malformed bases.
	let parsed: URL;
	try {
		parsed = new URL(kernelServerUrl);
	} catch {
		return;
	}
	parsed.port = String(backendPort);
	parsed.pathname = '';
	parsed.search = '';
	parsed.hash = '';
	const base = parsed.toString().replace(/\/+$/, '');
	const w = window as unknown as Record<string, unknown>;
	w.__KHAL_PACK_API_BASE__ = base;
	// Per-pack compatibility shims. Once every pack reads the generic global
	// these per-pack entries can be removed.
	if (appId === 'eugenia-metrics-pack' || appId === 'eugenia-metrics') {
		w.__EUGENIA_API_BASE__ = base;
	}
}

interface PackModule {
	default: ComponentType<Record<string, unknown>>;
	manifest?: { id: string; name: string; icon?: string };
}

interface DynamicAppBundleProps {
	windowId: string;
	meta?: Record<string, unknown>;
}

// Module-level cache: one *resolved module* per bundle URL. We deliberately
// cache the awaited result, not the in-flight Promise, because React 19's
// StrictMode double-mount cycle can strand the first mount's Promise mid-
// await: the cleanup runs before the await resolves, the second mount finds
// the same pending Promise in the cache, awaits it forever, and the window
// gets stuck on "Loading <appId>…" indefinitely even though the bundle URL
// resolves fine when imported directly (verified empirically in dev-local on
// 2026-05-18). Caching only resolved modules sidesteps the strand entirely:
// subsequent mounts either reuse the resolved module instantly, or trigger
// a fresh load if no resolved module exists yet.
const bundleCache = new Map<string, PackModule>();
const bundleInFlight = new Map<string, Promise<PackModule>>();

// One-shot flag for the Sentry heartbeat — we only want one
// `pack-loader-boot` info event per desktop session, the first time any
// pack bundle successfully loads. Proves the Sentry pipeline is alive
// end-to-end.
let sentryBootEmitted = false;

const relativeModuleSpecifierPattern =
	/\b(import|export)\s+(?:[\s\S]*?\s+from\s+)?(['"])(\.\.?\/[^'"]+)\2|\bimport\s*\(\s*(['"])(\.\.?\/[^'"]+)\4\s*\)/g;

/**
 * Blob-backed ESM modules have a `blob:` base URL. Browsers cannot resolve
 * `./chunk-*.js` from that non-hierarchical base, so before using the blob
 * fallback we preserve the pack contract by rebasing only relative module
 * specifiers against the original kernel-served bundle URL. Bare specifiers
 * (`react`, `@khal-os/*`) still resolve through the desktop import map.
 */
export function absolutizeRelativeModuleSpecifiersForBlobImport(
	code: string,
	bundleUrl: string,
): string {
	return code.replace(
		relativeModuleSpecifierPattern,
		(
			match,
			_keyword: string,
			quote: string | undefined,
			staticSpecifier: string | undefined,
			dynamicQuote: string | undefined,
			dynamicSpecifier: string | undefined,
		) => {
			const specifier = staticSpecifier ?? dynamicSpecifier;
			if (!specifier) return match;

			const absoluteSpecifier = new URL(specifier, bundleUrl).toString();
			return staticSpecifier
				? match.replace(
						`${quote}${staticSpecifier}${quote}`,
						`${quote}${absoluteSpecifier}${quote}`,
					)
				: `import(${dynamicQuote}${absoluteSpecifier}${dynamicQuote})`;
		},
	);
}

/**
 * Fetch a pack bundle and resolve it as an ESM module. Tries native dynamic
 * `import()` first and falls back to a same-origin `blob:` URL on any
 * failure. Results are cached per `bundleUrl`.
 */
const CORE_PACK_FALLBACK_IDS = new Set(['files', 'settings']);

function getCorePackFallbackBundleUrl(appId: string): string | null {
	if (!CORE_PACK_FALLBACK_IDS.has(appId)) return null;
	if (typeof window === 'undefined' || !window.location?.origin) return null;
	return new URL(`/core-apps/${appId}/bundle.mjs`, window.location.origin).toString();
}

function ensureCorePackStylesheet(appId: string): void {
	if (!CORE_PACK_FALLBACK_IDS.has(appId)) return;
	if (typeof document === 'undefined') return;
	const href = `/core-apps/${appId}/bundle.css`;
	const id = `khal-core-pack-css-${appId}`;
	if (document.getElementById(id)) return;
	const link = document.createElement('link');
	link.id = id;
	link.rel = 'stylesheet';
	link.href = href;
	document.head.appendChild(link);
}

function loadPackBundle(bundleUrl: string): Promise<PackModule> {
	const resolved = bundleCache.get(bundleUrl);
	if (resolved) return Promise.resolve(resolved);

	// De-dupe concurrent in-flight loads (same mount cycle), but never strand
	// the promise across StrictMode unmount/remount — bundleInFlight is cleared
	// the moment the Promise settles, success or failure.
	const inFlight = bundleInFlight.get(bundleUrl);
	if (inFlight) return inFlight;

	const promise = (async (): Promise<PackModule> => {
		// Strategy 1: native cross-origin dynamic import.
		try {
			const mod = (await import(/* @vite-ignore */ bundleUrl)) as PackModule;
			if (mod && typeof mod.default === 'function') return mod;
			throw new Error('pack bundle has no default export');
		} catch (nativeErr) {
			// Strategy 2: fetch → Blob URL fallback. Blob URLs are
			// same-origin by definition and sidestep any webview policy
			// around cross-origin ESM imports.
			const res = await fetch(bundleUrl);
			if (!res.ok) {
				throw new Error(
					`bundle fetch failed: ${res.status} ${res.statusText}`,
				);
			}
			const code = absolutizeRelativeModuleSpecifiersForBlobImport(
				await res.text(),
				bundleUrl,
			);
			const blob = new Blob([code], { type: 'application/javascript' });
			const blobUrl = URL.createObjectURL(blob);
			try {
				const mod = (await import(/* @vite-ignore */ blobUrl)) as PackModule;
				if (!mod || typeof mod.default !== 'function') {
					throw new Error(
						`pack bundle has no default export (native error: ${
							(nativeErr as Error)?.message ?? 'unknown'
						})`,
					);
				}
				return mod;
			} finally {
				URL.revokeObjectURL(blobUrl);
			}
		}
	})();

	bundleInFlight.set(bundleUrl, promise);
	promise.then(
		(mod) => {
			bundleCache.set(bundleUrl, mod);
			bundleInFlight.delete(bundleUrl);
		},
		() => {
			// Failed: clear in-flight so retry triggers a fresh attempt.
			bundleInFlight.delete(bundleUrl);
		},
	);
	return promise;
}

const containerStyle: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
	flexDirection: 'column',
	gap: 12,
	padding: 24,
	color: 'var(--khal-text-secondary, #888)',
	textAlign: 'center',
};

const retryButtonStyle: React.CSSProperties = {
	padding: '6px 14px',
	borderRadius: 6,
	border: '1px solid var(--khal-border, #333)',
	background: 'var(--khal-surface-elevated, #1f1f2e)',
	color: 'var(--khal-text, #ddd)',
	cursor: 'pointer',
	fontSize: 13,
};

export function DynamicAppBundle({ windowId, meta }: DynamicAppBundleProps) {
	const appId = (meta?.appId as string) || '';
	const [Component, setComponent] = useState<ComponentType<Record<string, unknown>> | null>(
		null,
	);
	const [error, setError] = useState<Error | null>(null);
	const [loading, setLoading] = useState(true);
	// Bumped by the Retry button to force a reload cycle after a failure.
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		setComponent(null);

		(async () => {
			try {
				const config = await getEnterpriseConfig();
				if (!config?.serverUrl) {
					throw new Error('not connected to instance');
				}
				// Expose the pack's BFF base URL on `window` BEFORE we dynamic-
				// import the bundle, so the pack's module-init code sees it on
				// first read (eugenia-metrics-pack#73 et al. compute API_BASE
				// once at module load). No-op for packs that don't read these
				// globals or have no backend.ports declared.
				injectPackApiBase(appId, config.serverUrl);
				// NOTE: Next.js treats `_apps` as a PRIVATE folder and refuses
				// to expose routes underneath it, which is why the original
				// pack loader 404'd in production. The kernel now serves
				// bundles at `/apps/<id>/<path>` and keeps a beforeFiles
				// rewrite from `/_apps/<id>/...` → `/apps/<id>/...` as
				// back-compat for older desktop builds.
				const kernelBundleUrl = `${config.serverUrl.replace(/\/$/, '')}/apps/${appId}/bundle.mjs`;
				const fallbackBundleUrl = getCorePackFallbackBundleUrl(appId);
				let mod: PackModule;
				try {
					mod = await loadPackBundle(kernelBundleUrl);
				} catch (kernelErr) {
					if (!fallbackBundleUrl) throw kernelErr;
					Sentry.captureException(kernelErr instanceof Error ? kernelErr : new Error(String(kernelErr)), {
						level: 'warning',
						tags: { scope: 'pack-loader-core-fallback', appId },
					});
					ensureCorePackStylesheet(appId);
					mod = await loadPackBundle(fallbackBundleUrl);
				}
				if (cancelled) return;
				setComponent(() => mod.default);
				setLoading(false);

				if (!sentryBootEmitted) {
					sentryBootEmitted = true;
					Sentry.captureMessage('pack-loader-boot', 'info');
				}
			} catch (err) {
				if (cancelled) return;
				const wrapped = err instanceof Error ? err : new Error(String(err));
				Sentry.captureException(wrapped, {
					tags: { scope: 'pack-loader', appId },
				});
				setError(wrapped);
				setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [appId, attempt]);

	const handleRetry = useCallback(() => {
		setAttempt((n) => n + 1);
	}, []);

	if (!appId) {
		return (
			<div style={containerStyle}>
				<div>Missing appId — cannot load bundle.</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div style={containerStyle}>
				<div>Loading {appId}…</div>
			</div>
		);
	}

	if (error) {
		return (
			<div style={containerStyle}>
				<div>Failed to load {appId}</div>
				<div style={{ fontSize: 12, opacity: 0.7, maxWidth: 480 }}>
					{error.message}
				</div>
				<button type="button" onClick={handleRetry} style={retryButtonStyle}>
					Retry
				</button>
			</div>
		);
	}

	if (!Component) {
		return (
			<div style={containerStyle}>
				<div>Pack bundle resolved without a component.</div>
			</div>
		);
	}

	return <Component windowId={windowId} meta={meta} />;
}
